function parseTaxYear(returnId: string): string {
  const match = (returnId || '').match(/^(\d{4})/);
  return match ? match[1] : '';
}

export function calcTitle(fileName: string, clientCode: string, returnId: string): string {
  if (/clnt/i.test(fileName)) {
    const dotCode = (clientCode || '').replace('-', '.');
    const taxYear = parseTaxYear(returnId);
    return `${dotCode} ${taxYear} Tax`;
  }
  return fileName.replace(/\.pdf$/i, '');
}

export function calcK1Title(cchFileName: string): string {
  const base = cchFileName.replace(/\.pdf$/i, '');
  const taxYear = base.match(/^(\d{4})/)?.[1] || '';
  const clientCodeMatch = base.match(/^\d{4}[A-Z]{2}\s[A-Z](\d+\.\d+)\s/);
  const clientCode = clientCodeMatch?.[1] || '';
  const k1Match = base.match(/K1[A-Z]\s+V\d+\s+(\d+)\s+(.+)$/);
  const partnerNum  = k1Match?.[1] || '';
  const partnerName = k1Match?.[2] || '';
  return `${clientCode} ${taxYear} K-1 ${partnerNum} ${partnerName}`.trim();
}

export type PrintFile = {
  BatchItemGuid: string;
  FileName: string;
};

export type PrintBatchResponse = {
  batchGuid: string;
  files: PrintFile[];
};

export type SafeSendUploadResult = {
  taxReturnId: string;
  reviewUrl: string;
};

export type MFilesUploadResult = {
  objId: number;
  title: string;
};

export async function submitPrintBatch(
  returnId: string,
  mode: 'return' | 'k1s' | 'safesend'
): Promise<PrintBatchResponse> {
  const resp = await fetch('/api/print-return-batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ returnId, mode }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to submit print batch (HTTP ${resp.status}). ${text}`);
  }
  const json = (await resp.json()) as PrintBatchResponse;
  if (!json?.batchGuid) throw new Error('Print batch response missing batchGuid.');
  return json;
}

export async function uploadFileToSafeSend(
  batchGuid: string,
  batchItemGuid: string,
  fileName: string,
  clientCode: string
): Promise<SafeSendUploadResult> {
  const resp = await fetch('/api/upload-to-safesend', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ batchGuid, batchItemGuid, fileName, clientCode }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`SafeSend upload failed (HTTP ${resp.status}). ${text}`);
  }
  return resp.json() as Promise<SafeSendUploadResult>;
}

export async function saveFilesToMFiles(
  batchGuid: string,
  files: Array<{ batchItemGuid: string; fileName: string }>,
  clientName: string,
  clientCode: string,
  jobName: string,
  returnId: string,
  isK1s: boolean
): Promise<MFilesUploadResult[]> {
  const resp = await fetch('/api/save-to-mfiles', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      batchGuid,
      batchItemGuids: files.map((f) => f.batchItemGuid),
      fileNames:      files.map((f) => f.fileName),
      clientName,
      clientCode,
      jobName,
      returnId,
      isK1s,
    }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`M-Files save failed (HTTP ${resp.status}). ${text}`);
  }
  return resp.json() as Promise<MFilesUploadResult[]>;
}

export async function downloadPrintFile(
  batchGuid: string,
  batchItemGuid: string,
  fileName: string
): Promise<Blob> {
  const resp = await fetch('/api/download-print-file', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ batchGuid, batchItemGuid, fileName }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to download print file (HTTP ${resp.status}). ${text}`);
  }
  return resp.blob();
}

export async function parseReturnLetters(
  batchGuid: string,
  batchItemGuid: string,
  fileName: string
): Promise<Blob> {
  const resp = await fetch('/api/parse-return-letters', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ batchGuid, batchItemGuid, fileName }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to parse return letters (HTTP ${resp.status}). ${text}`);
  }
  return resp.blob();
}